import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import {
  Users2, Zap, Music2, Mail, Instagram, Send, Phone, ChevronDown,
  ChevronUp, Check, X, Loader2, Plus, ExternalLink, Headphones,
  Sliders, Sparkles, Filter, ToggleLeft, ToggleRight, Info,
  Pencil, Trash2, Power, Building2, Clock, Palette, MessageSquare,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import api from "../utils/api";
import { usePublicAuth } from "../context/PublicAuthContext";
import { toast } from "sonner";

const DAW_ICONS = {
  "Ableton Live": "🎛️",
  "FL Studio":    "🎹",
  "Logic Pro":    "🍎",
  "Cubase":       "🎼",
  "Reason":       "🔌",
  "Studio One":   "⚡",
  "Bitwig Studio":"🌊",
  "Pro Tools":    "🎧",
  "Reaper":       "🔧",
  "Andere":       "🎵",
};

// ── Formular (Erstellen + Bearbeiten) ─────────────────────────────────────────
function CollabForm({ token, initialData, meta, onSaved, onCancel }) {
  const { t } = useTranslation();
  const isEdit = !!initialData?.id;
  const [form, setForm] = useState({
    active:      initialData?.active ?? true,
    headline:    initialData?.headline    || "",
    looking_for: initialData?.looking_for || [],
    daw:         initialData?.daw         || "",
    plugins:     initialData?.plugins     || "",
    description: initialData?.description || "",
    contact: {
      email:     initialData?.contact?.email     || "",
      instagram: initialData?.contact?.instagram || "",
      telegram:  initialData?.contact?.telegram  || "",
      phone:     initialData?.contact?.phone     || "",
    },
  });
  const [saving, setSaving] = useState(false);

  const toggleLF = (val) =>
    setForm(f => ({
      ...f,
      looking_for: f.looking_for.includes(val)
        ? f.looking_for.filter(x => x !== val)
        : [...f.looking_for, val],
    }));

  const hasContact = form.contact.email || form.contact.instagram || form.contact.telegram || form.contact.phone;

  const save = async () => {
    if (!form.headline.trim()) { toast.error(t("collab.err_headline")); return; }
    if (!hasContact)           { toast.error(t("collab.err_contact")); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/collabs/mine/${initialData.id}`, form, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await api.post("/collabs/mine", form, { headers: { Authorization: `Bearer ${token}` } });
      }
      toast.success(t("collab.toast_saved"));
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("collab.err_generic"));
    }
    setSaving(false);
  };

  const inputCls = "w-full bg-zinc-900/60 border border-white/[0.07] rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-blue-500/50 transition-colors";

  return (
    <div className="rounded-2xl overflow-hidden" style={{
      background: "linear-gradient(160deg, #0a1628 0%, #0d1f3c 60%, #070d1a 100%)",
      border: "1px solid rgba(59,130,246,0.25)",
      boxShadow: "0 16px 48px rgba(0,0,0,0.5)",
    }}>
      {/* Kopfzeile */}
      <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: "rgba(59,130,246,0.1)" }}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center"
            style={{ background: "rgba(59,130,246,0.15)", border: "1px solid rgba(59,130,246,0.2)" }}>
            <Sparkles size={15} className="text-blue-400" />
          </div>
          <p className="text-white font-bold text-sm">
            {isEdit ? t("collab.form_edit_title") : t("collab.form_create_title")}
          </p>
        </div>
        <button onClick={onCancel} className="text-zinc-600 hover:text-zinc-400 transition-colors">
          <X size={16} />
        </button>
      </div>

      <div className="px-6 pb-6 space-y-5">
        <div className="pt-5">
          {/* Aktiv-Toggle */}
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="text-white text-sm font-semibold">{t("collab.form_active_label")}</p>
              <p className="text-zinc-500 text-xs mt-0.5">{t("collab.form_active_desc")}</p>
            </div>
            <button onClick={() => setForm(f => ({ ...f, active: !f.active }))} data-testid="collab-active-toggle" className="transition-colors">
              {form.active
                ? <ToggleRight size={32} className="text-blue-400" />
                : <ToggleLeft  size={32} className="text-zinc-600" />}
            </button>
          </div>

          {/* Headline */}
          <label className="text-xs text-zinc-400 font-semibold uppercase tracking-wider">{t("collab.form_headline_label")}</label>
          <input
            data-testid="collab-headline"
            maxLength={100}
            value={form.headline}
            onChange={e => setForm(f => ({ ...f, headline: e.target.value }))}
            placeholder={t("collab.form_headline_ph")}
            className={inputCls + " mt-1.5"}
          />
          <p className="text-zinc-700 text-xs text-right mt-1">{form.headline.length}/100</p>
        </div>

        {/* Was ich suche */}
        <div className="space-y-2">
          <label className="text-xs text-zinc-400 font-semibold uppercase tracking-wider">{t("collab.form_looking_for_label")}</label>
          <div className="flex flex-wrap gap-2 mt-1.5">
            {meta.looking_for.map(opt => (
              <button
                key={opt}
                onClick={() => toggleLF(opt)}
                data-testid={`looking-for-${opt.toLowerCase().replace(/\//g, "-")}`}
                className="px-3 py-1.5 rounded-xl text-xs font-semibold transition-all"
                style={form.looking_for.includes(opt) ? {
                  background: "rgba(59,130,246,0.2)", border: "1px solid rgba(59,130,246,0.4)", color: "#93c5fd",
                } : {
                  background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)", color: "#71717a",
                }}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>

        {/* DAW */}
        <div className="space-y-1.5">
          <label className="text-xs text-zinc-400 font-semibold uppercase tracking-wider">{t("collab.form_daw_label")}</label>
          <select
            data-testid="collab-daw"
            value={form.daw}
            onChange={e => setForm(f => ({ ...f, daw: e.target.value }))}
            className={inputCls}
          >
            <option value="">{t("collab.form_daw_placeholder")}</option>
            {meta.daws.map(d => <option key={d} value={d}>{DAW_ICONS[d] || "🎵"} {d}</option>)}
          </select>
        </div>

        {/* Plugins */}
        <div className="space-y-1.5">
          <label className="text-xs text-zinc-400 font-semibold uppercase tracking-wider">{t("collab.form_plugins_label")}</label>
          <input
            data-testid="collab-plugins"
            maxLength={200}
            value={form.plugins}
            onChange={e => setForm(f => ({ ...f, plugins: e.target.value }))}
            placeholder={t("collab.form_plugins_ph")}
            className={inputCls}
          />
          <p className="text-zinc-700 text-xs">{t("collab.form_plugins_hint")}</p>
        </div>

        {/* Beschreibung – 800 Zeichen */}
        <div className="space-y-1.5">
          <label className="text-xs text-zinc-400 font-semibold uppercase tracking-wider">{t("collab.form_desc_label")}</label>
          <textarea
            data-testid="collab-description"
            maxLength={800}
            rows={4}
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            placeholder={t("collab.form_desc_ph")}
            className={inputCls + " resize-none"}
          />
          <p className="text-zinc-700 text-xs text-right">{form.description.length}/800</p>
        </div>

        {/* Kontakt */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <label className="text-xs text-zinc-400 font-semibold uppercase tracking-wider">{t("collab.form_contact_label")}</label>
            <span className="text-xs text-zinc-600 normal-case font-normal">{t("collab.form_contact_min")}</span>
          </div>
          <div className="rounded-xl p-3 space-y-2.5" style={{ background: "rgba(59,130,246,0.04)", border: "1px solid rgba(59,130,246,0.08)" }}>
            {[
              { icon: Mail,      key: "email",     type: "email", ph: "E-Mail",                    max: 120 },
              { icon: Instagram, key: "instagram", type: "text",  ph: "Instagram @handle",          max: 80  },
              { icon: Send,      key: "telegram",  type: "text",  ph: "Telegram @handle",           max: 80  },
              { icon: Phone,     key: "phone",     type: "text",  ph: "Telefon / WhatsApp (optional)", max: 40 },
            ].map(({ icon: Icon, key, type, ph, max }) => (
              <div key={key} className="flex items-center gap-2.5">
                <Icon size={13} className="text-zinc-500 flex-shrink-0" />
                <input
                  data-testid={`collab-contact-${key}`}
                  type={type} maxLength={max} value={form.contact[key]}
                  onChange={e => setForm(f => ({ ...f, contact: { ...f.contact, [key]: e.target.value } }))}
                  placeholder={ph}
                  className="flex-1 bg-transparent border-b border-white/[0.07] pb-1 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-blue-500/40 transition-colors"
                />
              </div>
            ))}
          </div>
          <div className="flex items-start gap-2 text-xs text-zinc-600">
            <Info size={11} className="flex-shrink-0 mt-0.5" />
            {t("collab.form_contact_hint")}
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-3 pt-1">
          <button
            onClick={save} disabled={saving}
            data-testid="collab-save-btn"
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-40"
            style={{ background: "linear-gradient(110deg, #2563eb, #1d4ed8)", border: "1px solid rgba(59,130,246,0.4)" }}
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            {t("collab.form_save_btn")}
          </button>
          <button
            onClick={onCancel} disabled={saving}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-zinc-400 hover:text-zinc-200 transition-all border border-white/[0.06]"
          >
            <X size={14} /> {t("collab.form_cancel_btn")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Eigene Gesuch-Zeile (Verwaltungsansicht) ─────────────────────────────────
function MyCollabRow({ collab, onEdit, onDelete, onToggle, deleting }) {
  const { t } = useTranslation();
  return (
    <div
      data-testid={`my-collab-row-${collab.id}`}
      className="flex items-center gap-3 px-4 py-3 rounded-xl"
      style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-white text-sm font-semibold truncate max-w-xs">{collab.headline || "—"}</p>
          <span className="text-[10px] px-2 py-0.5 rounded-full font-bold flex-shrink-0"
            style={collab.active ? {
              background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.2)", color: "#4ade80",
            } : {
              background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "#52525b",
            }}>
            {collab.active ? t("collab.active_badge") : t("collab.inactive_badge")}
          </span>
        </div>
        {collab.looking_for?.length > 0 && (
          <p className="text-zinc-600 text-xs mt-0.5">{collab.looking_for.join(" · ")}</p>
        )}
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <button
          onClick={onToggle}
          data-testid={`collab-toggle-${collab.id}`}
          title={t("collab.toggle_btn")}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-500 hover:text-blue-400 hover:bg-blue-500/10 transition-all"
        >
          <Power size={13} />
        </button>
        <button
          onClick={onEdit}
          data-testid={`collab-edit-${collab.id}`}
          title={t("collab.edit_btn")}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-500 hover:text-zinc-200 hover:bg-white/[0.06] transition-all"
        >
          <Pencil size={13} />
        </button>
        <button
          onClick={onDelete}
          disabled={deleting === collab.id}
          data-testid={`collab-delete-${collab.id}`}
          title={t("collab.delete_btn")}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-all disabled:opacity-40"
        >
          {deleting === collab.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
        </button>
      </div>
    </div>
  );
}

// ── Direktnachricht Compose (nur für Artists in Collab-Kontextbereich) ─────────
function MsgCompose({ contextType, contextRefId }) {
  const [text, setText]     = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent]     = useState(false);

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await api.post("/messages/start", {
        context_type:   contextType,
        context_ref_id: contextRefId,
        text:           text.trim(),
      });
      setSent(true);
      setText("");
      toast.success("Nachricht gesendet!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Senden");
    }
    setSending(false);
  };

  if (sent) {
    return (
      <div className="flex items-center gap-2 py-2.5 px-3 rounded-xl"
        style={{ background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.2)" }}>
        <Check size={12} className="text-blue-400 flex-shrink-0" />
        <p className="text-blue-300 text-xs font-semibold">Gesendet! Antwort kommt in deiner Glocke.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5">
        <MessageSquare size={11} className="text-blue-400" />
        <p className="text-xs font-semibold text-white">Direkte Nachricht</p>
        <span className="text-[10px] text-zinc-700 ml-auto">{text.length}/500</span>
      </div>
      <textarea
        value={text}
        onChange={e => setText(e.target.value.slice(0, 500))}
        placeholder="Schreib deine Nachricht…"
        rows={3}
        data-testid={`msg-compose-${contextRefId}`}
        className="w-full text-xs px-3 py-2 rounded-xl resize-none focus:outline-none focus:ring-1 focus:ring-blue-500/30"
        style={{ background: "rgba(59,130,246,0.05)", border: "1px solid rgba(59,130,246,0.15)", color: "#d4d4d8" }}
      />
      <button
        onClick={send}
        disabled={!text.trim() || sending}
        data-testid={`msg-send-btn-${contextRefId}`}
        className="w-full flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-bold transition-all disabled:opacity-40"
        style={{ background: "rgba(59,130,246,0.2)", border: "1px solid rgba(59,130,246,0.3)", color: "#93c5fd" }}
      >
        {sending ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
        {sending ? "Wird gesendet…" : "Nachricht senden"}
      </button>
    </div>
  );
}

// ── Collab-Karte (öffentliche Ansicht) ───────────────────────────────────────
function CollabCard({ item, highlighted, isOwn, isArtist }) {
  const { t } = useTranslation();
  const [showContact, setShowContact] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    if (highlighted && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlighted]);

  return (
    <div
      ref={cardRef}
      data-testid={`collab-card-${item.artist_slug || item.id}`}
      className="rounded-2xl overflow-hidden transition-all"
      style={{
        background: isOwn
          ? "linear-gradient(160deg, #0d2040 0%, #0a1628 100%)"
          : highlighted
          ? "linear-gradient(160deg, #0f2441 0%, #0a1a30 100%)"
          : "rgba(255,255,255,0.02)",
        border: isOwn
          ? "1px solid rgba(59,130,246,0.4)"
          : highlighted
          ? "1px solid rgba(59,130,246,0.35)"
          : "1px solid rgba(255,255,255,0.05)",
        boxShadow: isOwn ? "0 0 0 2px rgba(59,130,246,0.08)" : highlighted ? "0 0 0 2px rgba(59,130,246,0.15)" : "none",
      }}
    >
      {isOwn && (
        <div className="flex items-center gap-1.5 px-4 pt-3 pb-0">
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
            style={{ background: "rgba(59,130,246,0.15)", color: "#60a5fa", border: "1px solid rgba(59,130,246,0.25)" }}>
            {t("collab.own_badge")}
          </span>
        </div>
      )}
      <div className="p-4">
        {/* Artist Header */}
        <div className="flex items-start gap-3 mb-3">
          <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0"
            style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.07)" }}>
            {item.artist_image
              ? <img src={item.artist_image} alt={item.artist_name} className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center text-zinc-600"><Music2 size={18} /></div>
            }
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              {item.artist_slug
                ? <a href={`/artist/${item.artist_slug}`} target="_blank" rel="noreferrer"
                    className="text-white font-bold text-sm hover:text-blue-400 transition-colors flex items-center gap-1">
                    {item.artist_name} <ExternalLink size={10} className="text-zinc-600" />
                  </a>
                : <span className="text-white font-bold text-sm">{item.artist_name}</span>
              }
            </div>
            <p className="text-zinc-400 text-xs mt-0.5 font-medium">{item.headline}</p>
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {item.daw && (
            <span className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold"
              style={{ background: "rgba(139,92,246,0.1)", border: "1px solid rgba(139,92,246,0.2)", color: "#a78bfa" }}>
              <Headphones size={9} /> {DAW_ICONS[item.daw] || ""} {item.daw}
            </span>
          )}
          {(item.looking_for || []).map(lf => (
            <span key={lf} className="px-2.5 py-1 rounded-full text-[10px] font-bold"
              style={{ background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.15)", color: "#93c5fd" }}>
              {lf}
            </span>
          ))}
        </div>

        {/* Plugins */}
        {item.plugins && (
          <div className="flex items-start gap-1.5 mb-3">
            <Sliders size={10} className="text-zinc-600 flex-shrink-0 mt-0.5" />
            <p className="text-zinc-600 text-[11px]">{item.plugins}</p>
          </div>
        )}

        {/* Description */}
        {item.description && (
          <p className="text-zinc-500 text-xs leading-relaxed mb-3">{item.description}</p>
        )}

        {/* Kontakt Button */}
        {!isOwn && (
          <button
            onClick={() => setShowContact(s => !s)}
            data-testid={`collab-contact-btn-${item.artist_slug || item.id}`}
            className="w-full flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-bold transition-all"
            style={showContact ? {
              background: "rgba(59,130,246,0.15)", border: "1px solid rgba(59,130,246,0.3)", color: "#93c5fd",
            } : {
              background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)", color: "#71717a",
            }}
          >
            {isArtist
              ? <><MessageSquare size={12} />{showContact ? "Schließen" : "Kontaktieren"}</>
              : <>{showContact ? <ChevronUp size={12} /> : <ChevronDown size={12} />}{showContact ? t("collab.contact_hide") : t("collab.contact_show")}</>}
          </button>
        )}

        {showContact && !isOwn && (
          <div className="mt-3 p-3 rounded-xl space-y-3"
            style={{ background: "rgba(59,130,246,0.05)", border: "1px solid rgba(59,130,246,0.1)" }}>
            {/* Direktnachricht für eingeloggte Artists */}
            {isArtist && (
              <MsgCompose contextType="collab_finder" contextRefId={item.id} />
            )}
            {/* Kontaktdaten als Alternative */}
            {(item.contact?.email || item.contact?.instagram || item.contact?.telegram || item.contact?.phone) && (
              <div className={isArtist ? "pt-2 border-t border-white/[0.05] space-y-2" : "space-y-2"}>
                {isArtist && <p className="text-[10px] text-zinc-700">Oder extern kontaktieren:</p>}
                {item.contact?.email && (
                  <a href={`mailto:${item.contact.email}`}
                    className="flex items-center gap-2 text-xs text-zinc-300 hover:text-blue-400 transition-colors">
                    <Mail size={12} className="text-zinc-500" /> {item.contact.email}
                  </a>
                )}
                {item.contact?.instagram && (
                  <a href={`https://instagram.com/${item.contact.instagram.replace(/^@/, "")}`}
                    target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 text-xs text-zinc-300 hover:text-pink-400 transition-colors">
                    <Instagram size={12} className="text-zinc-500" /> {item.contact.instagram}
                  </a>
                )}
                {item.contact?.telegram && (
                  <a href={`https://t.me/${item.contact.telegram.replace(/^@/, "")}`}
                    target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 text-xs text-zinc-300 hover:text-sky-400 transition-colors">
                    <Send size={12} className="text-zinc-500" /> {item.contact.telegram}
                  </a>
                )}
                {item.contact?.phone && (
                  <span className="flex items-center gap-2 text-xs text-zinc-400">
                    <Phone size={12} className="text-zinc-500" /> {item.contact.phone}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Label-Anfragen Karte (grünes Farbschema) ─────────────────────────────────
function LabelRequestCard({ item, isArtist }) {
  const { t } = useTranslation();
  const [showContact, setShowContact] = useState(false);

  return (
    <div
      data-testid={`label-req-card-${item.label_slug || item.id}`}
      className="rounded-2xl overflow-hidden transition-all"
      style={{
        background: "linear-gradient(160deg, rgba(16,185,129,0.06) 0%, rgba(5,150,105,0.03) 100%)",
        border: "1px solid rgba(16,185,129,0.2)",
      }}
    >
      {/* Label-Badge oben */}
      <div className="flex items-center gap-1.5 px-4 pt-3 pb-0">
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1"
          style={{ background: "rgba(16,185,129,0.12)", color: "#34d399", border: "1px solid rgba(16,185,129,0.2)" }}>
          <Building2 size={9} /> {t("label_req.by_label")} {item.label_name}
        </span>
      </div>

      <div className="p-4">
        {/* Label Header */}
        <div className="flex items-start gap-3 mb-3">
          <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0"
            style={{ background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.15)" }}>
            {item.label_logo
              ? <img src={item.label_logo} alt={item.label_name} className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center">
                  <Building2 size={18} className="text-emerald-600" />
                </div>
            }
          </div>
          <div className="flex-1 min-w-0">
            {item.label_slug
              ? <a href={`/label/${item.label_slug}`} target="_blank" rel="noreferrer"
                  className="text-white font-bold text-sm hover:text-emerald-400 transition-colors flex items-center gap-1">
                  {item.label_name} <ExternalLink size={10} className="text-zinc-600" />
                </a>
              : <span className="text-white font-bold text-sm">{item.label_name}</span>
            }
            <p className="text-zinc-300 text-xs mt-0.5 font-medium">{item.headline}</p>
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {(item.looking_for || []).map(lf => (
            <span key={lf} className="px-2.5 py-1 rounded-full text-[10px] font-bold"
              style={{ background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.2)", color: "#34d399" }}>
              {lf}
            </span>
          ))}
        </div>

        {/* Stil + Deadline */}
        <div className="space-y-1.5 mb-3">
          {item.style && (
            <div className="flex items-center gap-1.5">
              <Palette size={10} className="text-zinc-600 flex-shrink-0" />
              <p className="text-zinc-500 text-[11px]">
                <span className="text-zinc-600 font-semibold">{t("label_req.style_label")}:</span> {item.style}
              </p>
            </div>
          )}
          {item.deadline && (
            <div className="flex items-center gap-1.5">
              <Clock size={10} className="text-zinc-600 flex-shrink-0" />
              <p className="text-zinc-500 text-[11px]">
                <span className="text-zinc-600 font-semibold">{t("label_req.deadline_label")}:</span> {item.deadline}
              </p>
            </div>
          )}
        </div>

        {/* Description */}
        {item.description && (
          <p className="text-zinc-500 text-xs leading-relaxed mb-3">{item.description}</p>
        )}

        {/* Kontakt Button */}
        <button
          onClick={() => setShowContact(s => !s)}
          data-testid={`label-req-contact-btn-${item.label_slug || item.id}`}
          className="w-full flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-bold transition-all"
          style={showContact ? {
            background: "rgba(16,185,129,0.15)", border: "1px solid rgba(16,185,129,0.3)", color: "#34d399",
          } : {
            background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)", color: "#71717a",
          }}
        >
          {isArtist
            ? <><MessageSquare size={12} />{showContact ? "Schließen" : "Label anschreiben"}</>
            : <>{showContact ? <ChevronUp size={12} /> : <ChevronDown size={12} />}{showContact ? t("label_req.contact_hide") : t("label_req.contact_show")}</>}
        </button>

        {showContact && (
          <div className="mt-3 p-3 rounded-xl space-y-3"
            style={{ background: "rgba(16,185,129,0.05)", border: "1px solid rgba(16,185,129,0.12)" }}>
            {/* Direktnachricht für eingeloggte Artists */}
            {isArtist && (
              <MsgCompose contextType="label_request" contextRefId={item.id} />
            )}
            {/* Kontaktdaten als Alternative */}
            {(item.contact?.email || item.contact?.instagram || item.contact?.telegram || item.contact?.phone) && (
              <div className={isArtist ? "pt-2 border-t border-white/[0.05] space-y-2" : "space-y-2"}>
                {isArtist && <p className="text-[10px] text-zinc-700">Oder extern kontaktieren:</p>}
                {item.contact?.email && (
                  <a href={`mailto:${item.contact.email}`}
                    className="flex items-center gap-2 text-xs text-zinc-300 hover:text-emerald-400 transition-colors">
                    <Mail size={12} className="text-zinc-500" /> {item.contact.email}
                  </a>
                )}
                {item.contact?.instagram && (
                  <a href={`https://instagram.com/${item.contact.instagram.replace(/^@/, "")}`}
                    target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 text-xs text-zinc-300 hover:text-pink-400 transition-colors">
                    <Instagram size={12} className="text-zinc-500" /> {item.contact.instagram}
                  </a>
                )}
                {item.contact?.telegram && (
                  <a href={`https://t.me/${item.contact.telegram.replace(/^@/, "")}`}
                    target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 text-xs text-zinc-300 hover:text-sky-400 transition-colors">
                    <Send size={12} className="text-zinc-500" /> {item.contact.telegram}
                  </a>
                )}
                {item.contact?.phone && (
                  <span className="flex items-center gap-2 text-xs text-zinc-400">
                    <Phone size={12} className="text-zinc-500" /> {item.contact.phone}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


export default function CollabFinderPage() {
  const { t } = useTranslation();
  const { isPublicAuth, publicUser, token, isPublicLoading } = usePublicAuth();
  const [searchParams] = useSearchParams();
  const [meta,           setMeta]           = useState({ daws: [], looking_for: [] });
  const [collabs,        setCollabs]        = useState([]);
  const [myCollabs,      setMyCollabs]      = useState([]);
  const [labelRequests,  setLabelRequests]  = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [filterDaw,      setFilterDaw]      = useState("");
  const [filterLF,       setFilterLF]       = useState("");
  const [showFilter,     setShowFilter]     = useState(false);
  const [activeTab,      setActiveTab]      = useState(
    searchParams.get("tab") === "labels" ? "labels" : "artists"
  );
  // Form state
  const [showForm,   setShowForm]   = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleting,   setDeleting]   = useState(null);
  const highlight = searchParams.get("highlight") || "";
  const isArtist = !!(publicUser?.artist_ids?.length || publicUser?.artist_id);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [metaRes, collabsRes, mineRes, labelRes] = await Promise.all([
        api.get("/collabs/meta"),
        isArtist ? api.get("/collabs", {
          headers: { Authorization: `Bearer ${token}` },
          params: { daw: filterDaw || undefined, looking_for: filterLF || undefined },
        }) : Promise.resolve({ data: [] }),
        isArtist ? api.get("/collabs/mine", {
          headers: { Authorization: `Bearer ${token}` },
        }) : Promise.resolve({ data: [] }),
        isArtist ? api.get("/label-requests", {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      setMeta(metaRes.data);
      setCollabs(collabsRes.data);
      setMyCollabs(Array.isArray(mineRes.data) ? mineRes.data : (mineRes.data?.id ? [mineRes.data] : []));
      setLabelRequests(Array.isArray(labelRes.data) ? labelRes.data : []);
    } catch {}
    setLoading(false);
  }, [token, isArtist, filterDaw, filterLF]);

  useEffect(() => {
    if (!isPublicLoading) loadAll();
  }, [isPublicLoading, loadAll]);

  const handleDelete = async (id) => {
    if (!window.confirm(t("collab.form_delete_confirm"))) return;
    setDeleting(id);
    try {
      await api.delete(`/collabs/mine/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("collab.toast_deleted"));
      setMyCollabs(prev => prev.filter(c => c.id !== id));
      setCollabs(prev => prev.filter(c => c.id !== id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("collab.err_generic"));
    }
    setDeleting(null);
  };

  const handleToggle = async (id) => {
    try {
      const res = await api.patch(`/collabs/mine/${id}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(res.data.active ? t("collab.toast_on") : t("collab.toast_off"));
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("collab.err_generic"));
    }
  };

  const openCreate = () => { setEditTarget(null); setShowForm(true); };
  const openEdit   = (collab) => { setEditTarget(collab); setShowForm(true); };
  const closeForm  = () => { setShowForm(false); setEditTarget(null); };

  if (isPublicLoading) return (
    <div className="min-h-screen bg-[#060606] flex items-center justify-center">
      <Loader2 size={24} className="animate-spin text-zinc-600" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#060606] text-white">
      <Helmet>
        <title>{`${t("collab.page_title")} | Artist Studio – HUWND`}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Header />

      {/* Hero */}
      <div className="relative border-b border-white/[0.04] overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/3 w-96 h-32 bg-blue-600/6 rounded-full blur-3xl" />
          <div className="absolute top-0 right-1/4 w-64 h-24 bg-violet-600/4 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-10">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.2)" }}>
              <Users2 size={20} className="text-blue-400" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] mb-0.5" style={{ color: "rgba(96,165,250,0.6)" }}>
                {t("collab.page_subtitle")}
              </p>
              <h1 className="text-3xl font-black text-white tracking-tight">{t("collab.page_title")}</h1>
            </div>
            <Link to="/artist-studio"
              className="ml-auto text-xs text-zinc-600 hover:text-zinc-400 transition-colors flex items-center gap-1">
              {t("collab.back_studio")}
            </Link>
          </div>
          <p className="text-zinc-500 text-sm mt-4 max-w-xl">{t("collab.page_desc")}</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-8">

        {/* Nicht eingeloggt */}
        {!isPublicAuth && (
          <div className="max-w-xl mx-auto text-center py-16 space-y-4">
            <div className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center"
              style={{ background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.15)" }}>
              <Users2 size={24} className="text-blue-400/60" />
            </div>
            <h2 className="text-xl font-bold text-white">{t("collab.not_auth_title")}</h2>
            <p className="text-zinc-500 text-sm">{t("collab.not_auth_desc")}</p>
            <Link to="/artist-studio"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-all"
              style={{ background: "linear-gradient(110deg, #2563eb, #1d4ed8)", border: "1px solid rgba(59,130,246,0.4)" }}>
              {t("collab.not_auth_btn")} <Zap size={13} />
            </Link>
          </div>
        )}

        {isPublicAuth && !isArtist && (
          <div className="max-w-xl mx-auto text-center py-16 space-y-4">
            <div className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center"
              style={{ background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.15)" }}>
              <Users2 size={24} className="text-blue-400/60" />
            </div>
            <h2 className="text-xl font-bold text-white">{t("collab.no_access_title")}</h2>
            <p className="text-zinc-500 text-sm">{t("collab.no_access_desc")}</p>
            <Link to="/artist-studio"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-all"
              style={{ background: "linear-gradient(110deg, #2563eb, #1d4ed8)", border: "1px solid rgba(59,130,246,0.4)" }}>
              {t("collab.no_access_btn")}
            </Link>
          </div>
        )}

        {isPublicAuth && isArtist && (
          <>
            {/* ── Meine Gesuche ── */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles size={14} className="text-blue-400" />
                  <span className="text-white text-sm font-bold">
                    {t("collab.my_requests_title")}
                    {myCollabs.length > 0 && (
                      <span className="ml-2 text-zinc-500 font-normal">({myCollabs.length})</span>
                    )}
                  </span>
                </div>
                {!showForm && (
                  <button
                    onClick={openCreate}
                    data-testid="collab-new-btn"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-white transition-all"
                    style={{ background: "linear-gradient(110deg, #2563eb, #1d4ed8)", border: "1px solid rgba(59,130,246,0.4)" }}
                  >
                    <Plus size={12} /> {t("collab.new_request_btn")}
                  </button>
                )}
              </div>

              {/* Formular */}
              {showForm && (
                <CollabForm
                  token={token}
                  initialData={editTarget}
                  meta={meta}
                  onSaved={() => { closeForm(); loadAll(); }}
                  onCancel={closeForm}
                />
              )}

              {/* Eigene Gesuche Liste */}
              {myCollabs.length === 0 && !showForm ? (
                <div className="text-center py-6 rounded-xl" style={{ background: "rgba(255,255,255,0.01)", border: "1px solid rgba(255,255,255,0.04)" }}>
                  <p className="text-zinc-600 text-sm">{t("collab.no_requests")}</p>
                  <p className="text-zinc-700 text-xs mt-1">{t("collab.no_requests_hint")}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {myCollabs.map(collab => (
                    <MyCollabRow
                      key={collab.id}
                      collab={collab}
                      onEdit={() => openEdit(collab)}
                      onDelete={() => handleDelete(collab.id)}
                      onToggle={() => handleToggle(collab.id)}
                      deleting={deleting}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* ── Tabs ── */}
            <div className="flex gap-1 p-1 rounded-2xl" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
              <button
                onClick={() => setActiveTab("artists")}
                data-testid="tab-artists"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold transition-all"
                style={activeTab === "artists" ? {
                  background: "rgba(59,130,246,0.15)", border: "1px solid rgba(59,130,246,0.3)", color: "#93c5fd",
                } : { color: "#52525b" }}
              >
                <Users2 size={12} /> {t("collab.active_label")}
                {collabs.length > 0 && (
                  <span className="px-1.5 py-0.5 rounded-full text-[10px]"
                    style={{ background: "rgba(59,130,246,0.15)", color: "#60a5fa" }}>
                    {collabs.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab("labels")}
                data-testid="tab-labels"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold transition-all"
                style={activeTab === "labels" ? {
                  background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.25)", color: "#34d399",
                } : { color: "#52525b" }}
              >
                <Building2 size={12} /> {t("label_req.section_title")}
                {labelRequests.length > 0 && (
                  <span className="px-1.5 py-0.5 rounded-full text-[10px]"
                    style={{ background: "rgba(16,185,129,0.12)", color: "#34d399" }}>
                    {labelRequests.length}
                  </span>
                )}
              </button>
            </div>

            {/* ── Artist-Gesuche Tab ── */}
            {activeTab === "artists" && (
              <>
                {/* Filter */}
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => setShowFilter(s => !s)}
                    data-testid="collab-filter-toggle"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all"
                    style={{
                      background: showFilter ? "rgba(59,130,246,0.15)" : "rgba(255,255,255,0.04)",
                      border: showFilter ? "1px solid rgba(59,130,246,0.3)" : "1px solid rgba(255,255,255,0.07)",
                      color: showFilter ? "#93c5fd" : "#71717a",
                    }}
                  >
                    <Filter size={11} /> {t("collab.filter_btn")}
                    {(filterDaw || filterLF) && <span className="w-1.5 h-1.5 rounded-full bg-blue-400 inline-block" />}
                  </button>
                  {(filterDaw || filterLF) && (
                    <button onClick={() => { setFilterDaw(""); setFilterLF(""); }}
                      className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors">
                      {t("collab.filter_reset")}
                    </button>
                  )}
                </div>

                {showFilter && (
                  <div className="flex flex-wrap gap-3 p-4 rounded-xl"
                    style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}>
                    <select value={filterDaw} onChange={e => setFilterDaw(e.target.value)} data-testid="filter-daw"
                      className="bg-zinc-900 border border-white/[0.07] rounded-xl px-3 py-2 text-xs text-zinc-300 focus:outline-none">
                      <option value="">{t("collab.filter_all_daws")}</option>
                      {meta.daws.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <select value={filterLF} onChange={e => setFilterLF(e.target.value)} data-testid="filter-looking-for"
                      className="bg-zinc-900 border border-white/[0.07] rounded-xl px-3 py-2 text-xs text-zinc-300 focus:outline-none">
                      <option value="">{t("collab.filter_all_lf")}</option>
                      {meta.looking_for.map(lf => <option key={lf} value={lf}>{lf}</option>)}
                    </select>
                  </div>
                )}

                {loading ? (
                  <div className="flex items-center justify-center py-12 gap-2 text-zinc-600 text-sm">
                    <Loader2 size={16} className="animate-spin" /> {t("collab.loading")}
                  </div>
                ) : collabs.length === 0 ? (
                  <div className="text-center py-12 space-y-2">
                    <Users2 size={28} className="text-zinc-700 mx-auto mb-3" />
                    <p className="text-zinc-500 text-sm font-semibold">{t("collab.empty_title")}</p>
                    <p className="text-zinc-700 text-xs">{t("collab.empty_hint")}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {collabs.map(item => (
                      <CollabCard
                        key={item.id || item.auth0_id}
                        item={item}
                        isOwn={item.auth0_id === publicUser?.auth0_id}
                        highlighted={!!(highlight && item.artist_slug === highlight)}
                        isArtist={isArtist}
                      />
                    ))}
                  </div>
                )}
              </>
            )}

            {/* ── Label-Aufträge Tab ── */}
            {activeTab === "labels" && (
              <>
                {loading ? (
                  <div className="flex items-center justify-center py-12 gap-2 text-zinc-600 text-sm">
                    <Loader2 size={16} className="animate-spin" /> {t("label_req.loading")}
                  </div>
                ) : labelRequests.length === 0 ? (
                  <div className="text-center py-12 space-y-2">
                    <Building2 size={28} className="text-emerald-900 mx-auto mb-3" />
                    <p className="text-zinc-500 text-sm font-semibold">{t("label_req.empty_title")}</p>
                    <p className="text-zinc-700 text-xs">{t("label_req.empty_hint")}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {labelRequests.map(item => (
                      <LabelRequestCard key={item.id} item={item} isArtist={isArtist} />
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}
